/**
 * Contact form helpers: newsletter opt-in side-submit, submit loading.
 * Hooks: [data-contact-form], [data-newsletter-opt-in], [data-contact-submit]
 * Phone formatting: assets/relic-phone-format.js ([data-phone-format])
 *
 * Newsletter signup uses a hidden native {% form 'customer' %} (see
 * relic-newsletter-customer-form-shell.liquid) submitted via iframe with
 * Shopify CAPTCHA protection (required since 2024 for programmatic submits).
 */

const LOADING_RESET_MS = 10000;
const NEWSLETTER_SUBMIT_TIMEOUT_MS = 15000;

function setSubmitLoading(button, isLoading) {
  if (!button) return;

  if (isLoading) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    return;
  }

  button.removeAttribute('aria-busy');
  button.disabled = false;
}

function findNewsletterCustomerForm(contactForm) {
  const scope = contactForm.closest('.shopify-section, [data-relic-custom-order-modal]');
  if (!scope) return null;

  return scope.querySelector('form.relic-newsletter-customer-form');
}

function populateNewsletterCustomerForm(contactForm, customerForm, email) {
  const emailField =
    customerForm.querySelector('[data-relic-newsletter-email-field]') ||
    customerForm.querySelector('[name="contact[email]"]');

  if (emailField) {
    emailField.value = email;
  }

  const firstNameInput = contactForm.querySelector(
    '[name="contact[First Name]"], [name="contact[first_name]"]'
  );
  const lastNameInput = contactForm.querySelector(
    '[name="contact[Last Name]"], [name="contact[last_name]"]'
  );
  const firstNameField = customerForm.querySelector('[data-relic-newsletter-first-name-field]');
  const lastNameField = customerForm.querySelector('[data-relic-newsletter-last-name-field]');

  if (firstNameField) {
    firstNameField.value = firstNameInput?.value || '';
  }

  if (lastNameField) {
    lastNameField.value = lastNameInput?.value || '';
  }
}

function submitCustomerFormWithCaptcha(customerForm, onReady) {
  if (
    window.Shopify &&
    window.Shopify.captcha &&
    typeof window.Shopify.captcha.protect === 'function'
  ) {
    window.Shopify.captcha.protect(customerForm, onReady);
    return;
  }

  onReady();
}

function submitNewsletterSignup(contactForm, email) {
  return new Promise(function (resolve) {
    const customerForm = findNewsletterCustomerForm(contactForm);

    if (!customerForm) {
      resolve(false);
      return;
    }

    populateNewsletterCustomerForm(contactForm, customerForm, email);

    const iframeName = 'relic-newsletter-' + Date.now();
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';

    const previousTarget = customerForm.target;
    let settled = false;
    let hasSubmitted = false;

    function finish(success) {
      if (settled) return;
      settled = true;
      customerForm.target = previousTarget;
      iframe.remove();
      resolve(success);
    }

    iframe.addEventListener('load', function () {
      if (!hasSubmitted) return;
      finish(true);
    });

    document.body.appendChild(iframe);

    submitCustomerFormWithCaptcha(customerForm, function () {
      customerForm.target = iframeName;
      hasSubmitted = true;
      customerForm.submit();
    });

    window.setTimeout(function () {
      finish(hasSubmitted);
    }, NEWSLETTER_SUBMIT_TIMEOUT_MS);
  });
}

function bindContactForm(form) {
  if (form.dataset.contactFormBound === 'true') return;
  form.dataset.contactFormBound = 'true';

  form.addEventListener('submit', function (event) {
    if (!form.checkValidity()) return;

    const button = form.querySelector('[data-contact-submit]');
    const optIn = form.querySelector('[data-newsletter-opt-in]');
    const emailInput = form.querySelector('[name="contact[email]"]');

    if (optIn && optIn.checked && emailInput && emailInput.value) {
      event.preventDefault();
      setSubmitLoading(button, true);

      submitNewsletterSignup(form, emailInput.value).finally(function () {
        submitCustomerFormWithCaptcha(form, function () {
          form.submit();
        });
      });
      return;
    }

    setSubmitLoading(button, true);

    window.setTimeout(function () {
      if (button && button.getAttribute('aria-busy') === 'true') {
        setSubmitLoading(button, false);
      }
    }, LOADING_RESET_MS);
  });
}

function initContactFormsInRoot(root) {
  root.querySelectorAll('[data-contact-form]').forEach(bindContactForm);
}

function initRelicContactForm() {
  initContactFormsInRoot(document);
  document.addEventListener('shopify:section:load', function (event) {
    initContactFormsInRoot(event.target);
  });
}

window.RelicContactForm = {
  initInRoot: initContactFormsInRoot,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRelicContactForm);
} else {
  initRelicContactForm();
}
